import { capitalize } from 'lodash/string';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';
import {
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceArea,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
} from 'recharts';
import { getOrgTermsContent } from '../../../content/utils.js';
import styles from './StakeholdersMap.module.css';
import { selectUserPermissions } from '../../../modules/auth.js';
import { updateStakeholderMapPosition } from '../../../modules/stakeholders.js';
import { isAuthorized } from '../../AuthorizedAction.js';
import { retrieveStakeholdersHistory } from '../../../modules/stakeholdersHistory.js';

const mapStateToProps = (state) => ({
  permissions: selectUserPermissions(state),
});

const mapDispatchToProps = {
  updateStakeholderMapPosition,
  retrieveStakeholdersHistory,
};

function StakeholdersMap(props) {
  const {
    bottomLeftQuadrantLabel,
    topLeftQuadrantLabel,
    bottomRightQuadrantLabel,
    topRightQuadrantLabel,
  } = props.project;

  function renderQuadrantLabel(value, props) {
    return (
      <ReferenceArea
        fill="none"
        label={{
          fill: 'var(--color--curious-blue)',
          fontWeight: 600,
          fontSize: 16,
          value,
        }}
        {...props}
      />
    );
  }

  const bottomLeftQuadrant = renderQuadrantLabel(
    capitalize(bottomLeftQuadrantLabel),
    {
      x1: 0,
      x2: 0.5,
      y1: 0,
      y2: 0.5,
    }
  );

  const topLeftQuadrant = renderQuadrantLabel(
    capitalize(topLeftQuadrantLabel),
    {
      x1: 0,
      x2: 0.5,
      y1: 0.5,
      y2: 1,
    }
  );

  const bottomRightQuadrant = renderQuadrantLabel(
    capitalize(bottomRightQuadrantLabel),
    {
      x1: 0.5,
      x2: 1,
      y1: 0,
      y2: 0.5,
    }
  );

  const topRightQuadrant = renderQuadrantLabel(
    capitalize(topRightQuadrantLabel),
    {
      x1: 0.5,
      x2: 1,
      y1: 0.5,
      y2: 1,
    }
  );

  // Create a local copy of `stakeholders` that we will mutate
  const stakeholders = useMemo(() => {
    return props.stakeholders
      .filter(
        ({ influence_position, interest_position }) =>
          Number.isFinite(influence_position) &&
          Number.isFinite(interest_position)
      )
      .map((stakeholder) => ({ ...stakeholder }));
  }, [props.stakeholders]);

  const [transitioning, setTransitioning] = useState(false);

  const checkAuthorize = isAuthorized(
    'update',
    'stakeholder',
    props.permissions
  );

  useEffect(() => {
    setTransitioning(true);
    setTimeout(() => setTransitioning(false), 0);
  }, [props.expanded]);

  // Used to trigger render upon mutation of `stakeholders`
  // https://reactjs.org/docs/hooks-faq.html#is-there-something-like-forceupdate
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);

  const dragItemIndex = useRef(null);

  const startDrag = useCallback((entry, index) => {
    if (props.project.archived) {
      window.alert(getOrgTermsContent('archivedProjectMessage'));
    } else {
      dragItemIndex.current = index;
    }
  }, []);

  const drag = useCallback(
    (chartEvent) => {
      const index = dragItemIndex.current;
      if (!chartEvent || index === null) return;

      stakeholders[index].interest_position = chartEvent.xValue;
      stakeholders[index].influence_position = chartEvent.yValue;
      forceUpdate();
    },
    [stakeholders]
  );

  const endDrag = useCallback(() => {
    const { updateStakeholderMapPosition, retrieveStakeholdersHistory } = props;

    const index = dragItemIndex.current;
    if (index === null) return;

    dragItemIndex.current = null;
    const { id, influence_position, interest_position } = stakeholders[index];
    const stakeholder = {
      ...stakeholders[index],
      influence_position,
      interest_position,
    };

    updateStakeholderMapPosition(id, stakeholder).then(() =>
      retrieveStakeholdersHistory(props.project.id)
    );
  }, [stakeholders]);

  const showDefaultAxisYName = (name, nativeColumn) => {
    if (nativeColumn === 'influence') {
      return nativeColumn === name ? 'Influence' : name;
    }
  };

  const showDefaultAxisXName = (name, nativeColumn) => {
    if (nativeColumn === 'interest') {
      return nativeColumn === name ? 'Interest' : name;
    }
  };

  return (
    <React.Fragment>
      <div
        style={{ padding: '30px 30px 0 30px' }}
        className="flex flex-justify"
      >
        <h2>Stakeholder Map</h2>
      </div>
      <div
        className={`${styles.stakeholdersMapContainer} component component-hide-border`}
      >
        {!transitioning && (
          <div className={styles.stakeholdersMap}>
            {/* Influence block */}
            <div className={`${styles.mapCell} ${styles.influenceHeading}`}>
              <span className={styles.influenceHeadingTitle}>
                {props.project.stakeholderFields.map((field) => (
                  <span key={field.id}>
                    {capitalize(
                      showDefaultAxisYName(field.name, field.nativeColumn)
                    )}
                  </span>
                ))}
                <span className="m-l-8">(y)</span>
              </span>
            </div>
            <div className={`${styles.mapCell} ${styles.influenceHigh}`}>
              <span
                className={`sentiment-dot sentiment-dot-lg-vertical sentiment-dot-high ${styles.sentimentDotHighInfluence}`}
              />
              <div className={styles.influenceTitleHigh}>High</div>
            </div>
            <div className={`${styles.mapCell} ${styles.influenceMedium}`}>
              <span
                className={`sentiment-dot sentiment-dot-lg-vertical sentiment-dot-medium ${styles.sentimentDotMediumInfluence}`}
              />
              <div className={styles.influenceTitleMedium}>Medium</div>
            </div>
            <div className={`${styles.mapCell} ${styles.influenceLow}`}>
              <span
                className={`sentiment-dot sentiment-dot-lg-vertical sentiment-dot-low ${styles.sentimentDotLowInfluence}`}
              />
              <div className={styles.influenceTitleLow}>Low</div>
            </div>

            {/* Interest block */}
            <div className={`${styles.mapCell} ${styles.interestHeading}`}>
              <span className={styles.interestHeadingTitle}>
                {props.project.stakeholderFields.map((field) => (
                  <span key={field.id}>
                    {capitalize(
                      showDefaultAxisXName(field.name, field.nativeColumn)
                    )}
                  </span>
                ))}{' '}
                (x)
              </span>
            </div>
            <div className={`${styles.mapCell} ${styles.interestHigh}`}>
              <span className="sentiment-dot sentiment-dot-lg sentiment-dot-high" />
              <div className={styles.interestTitle}>High</div>
            </div>
            <div className={`${styles.mapCell} ${styles.interestMedium}`}>
              <span className="sentiment-dot sentiment-dot-lg sentiment-dot-medium" />
              <div className={styles.interestTitle}>Medium</div>
            </div>
            <div className={`${styles.mapCell} ${styles.interestLow}`}>
              <span className="sentiment-dot sentiment-dot-lg sentiment-dot-low" />
              <div className={styles.interestTitle}>Low</div>
            </div>
            <div className={styles.chartContainer}>
              <ResponsiveContainer aspect={1.3} debounce={500}>
                <ScatterChart
                  margin={{
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                  }}
                  onMouseMove={checkAuthorize ? drag : null}
                  onMouseLeave={checkAuthorize ? endDrag : null}
                >
                  <CartesianGrid
                    horizontalPoints={['50%']}
                    verticalPoints={['50%']}
                    stroke="var(--color--silver)"
                  />
                  <XAxis
                    dataKey="interest_position"
                    domain={[0, 1]}
                    hide
                    type="number"
                  />
                  <YAxis
                    dataKey="influence_position"
                    domain={[0, 1]}
                    hide
                    type="number"
                  />
                  {bottomLeftQuadrant}
                  {topLeftQuadrant}
                  {bottomRightQuadrant}
                  {topRightQuadrant}
                  <Scatter
                    data={stakeholders}
                    isAnimationActive={false}
                    onMouseDown={checkAuthorize ? startDrag : null}
                    onMouseUp={checkAuthorize ? endDrag : null}
                  >
                    {stakeholders.map(({ id }) => {
                      const fill =
                        id === props.highlightedStakeholderId
                          ? 'var(--color--dark-sky-blue)'
                          : 'var(--color--dark-blue-grey)';
                      return (
                        <Cell
                          className="map-point"
                          fill={fill}
                          key={id}
                          size={16}
                          sizeType="diameter"
                        />
                      );
                    })}
                    <LabelList
                      className="map-label"
                      dataKey="name"
                      position="right"
                      offset={14}
                    />
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(StakeholdersMap);
